import { useCached } from '@/utils/react/state'
import { useEffect } from 'react'
import { useBlocker } from 'react-router-dom'
import { ref } from 'valtio'

import { DragHandle } from '@tiptap/extension-drag-handle-react'
import { EditorContent, useEditor } from '@tiptap/react'

import { gridState, gridStateActions, useGridSnap } from '../../grid/grid.store'
import { handleEditorFocus } from './tiptap/tiptap-focus'
import { CellClickOutsideController } from '../cell.hooks'

import { getContentForTextCell } from './content.helpers'
import { TIPTAP_CLASSES, TIPTAP_EXTENSIONS } from './tiptap/tiptap-config'

import { useCellDerivedState, useCellState } from '../CellContext'

import { CellUpload } from '../upload/CellUpload'
import { EditorConflictDialog } from './CellContentEditorConflict'

import { CellToolbar } from '../toolbar/CellToolbar'
import { CellToolbarEditorItems } from '../toolbar/CellToolbar.editoritems'
import {
  CellToolbarItemDiscard,
  CellToolbarItemSave,
} from '../toolbar/CellToolbar.items'

export function CellContentEditor() {
  const { cell } = useCellState()

  const { isReadOnly } = useCellDerivedState()

  const cellContentAsHTML = getContentForTextCell(cell?.content)

  const editor = useEditor({
    extensions: TIPTAP_EXTENSIONS,
    content: cellContentAsHTML,
    editable: !isReadOnly,
    editorProps: {
      attributes: {
        class:
          TIPTAP_CLASSES.LIVE +
          ' ' +
          // Outlines: default, focused, when group's drag-handle is active
          'outline-2 outline-neutral-500 focus:outline-blue-500 group-active:outline-blue-500',
        // a11y properties
        role: 'textbox',
        'aria-multiline': 'true',
        'aria-label': 'Cell content',
      },
    },
    onCreate({ editor }) {
      // Store closure so `getHTML()` can be accessed outside this component
      gridState.editor.getHTMLref = ref(() => editor.getHTML())

      // Run focus helper
      handleEditorFocus(editor)
    },

    onUpdate({ editor }) {
      // TipTap does not track dirty state
      // So we calculate it manually and store in state
      const initialContent = JSON.stringify(cellContentAsHTML)
      const currentContent = JSON.stringify(editor.getHTML())
      gridState.editor.isDirty = initialContent !== currentContent
    },

    onDestroy() {
      // Reset state
      gridStateActions.editor.resetEditor()
    },
  })

  return (
    <CellUpload>
      <EditorContent
        editor={editor}
        role="none" // a11y: purely a wrapper
        className="group relative w-full h-full rounded-sm isolate"
      />

      <DragHandle
        editor={editor}
        nested={true}
        onElementDragEnd={() => {
          // Focus editor after dragged handle is dropped
          editor.commands.focus(null, { scrollIntoView: false })
        }}
      >
        <span
          onClick={() => {
            // Focus editor when handle is clicked (not dragged)
            editor.commands.focus(null, { scrollIntoView: false })
          }}
        >
          ⠿
        </span>
      </DragHandle>

      <CellClickOutsideController
        handler={gridStateActions.editor.saveOrDiscard}
      />

      <EditorUnsavedController />

      <EditorConflictController />

      <EditorConflictDialog
        onOverwrite={gridStateActions.editor.saveForce}
        onDiscard={gridStateActions.editor.discard}
      />

      <CellToolbar
        onKeyTab={() => {
          editor.commands.focus(null, { scrollIntoView: false })
        }}
      >
        <CellToolbarEditorItems editor={editor} />

        <CellToolbarItemDiscard onClick={gridStateActions.editor.discard} />

        <CellToolbarItemSave onClick={gridStateActions.editor.save} />
      </CellToolbar>
    </CellUpload>
  )
}

/**
 * Headless controller component that watches the editor for unsaved changes,
 * blocks navigation and prompts the user to confirm.
 *
 * Handles both browser and react-router navigation.
 */
function EditorUnsavedController() {
  const {
    editor: { isDirty },
  } = useGridSnap()

  const blocker = useBlocker(isDirty)

  useEffect(() => {
    if (!isDirty) return

    // Handle react-router navigation
    if (blocker.state === 'blocked') {
      const confirmed = window.confirm(
        'Your changes have not been saved, do you wish to leave the page?',
      )
      if (confirmed) {
        blocker.proceed()
      } else {
        blocker.reset()
      }
      return
    }

    // Handle browser navigation
    const handleBeforeUnload = (event: BeforeUnloadEvent) => {
      event.preventDefault()
      event.returnValue = '' // Required by some browsers to trigger dialog
    }

    window.addEventListener('beforeunload', handleBeforeUnload)
    return () => window.removeEventListener('beforeunload', handleBeforeUnload)
  }, [blocker, isDirty])

  return null
}

/**
 * Checks if a cell has become stale while the user is editing it.
 * Meaning the cells content was updated on the server by someone else.
 */
function EditorConflictController() {
  const { cell } = useCellState()

  const cellUpdatedAt = cell?.updatedAt
  const initialUpdatedAt = useCached(cellUpdatedAt)

  useEffect(() => {
    if (!cellUpdatedAt || !initialUpdatedAt) return

    if (cellUpdatedAt > initialUpdatedAt) {
      gridState.editor.isConflicting = true
    }
  }, [initialUpdatedAt, cellUpdatedAt])

  return null
}
